import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import pa from './ProductActivityPage.module.css'
import { useRefetch } from '../lib/connection'
import o from '../components/Overlay.module.css'
import styles from './QualityControlPage.module.css'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { useLayer } from '../components/layers'
import { PendingButton, useSlow, inertClass, SkeletonRows } from '../components/pending'
import { openableRow } from '../lib/openableRow'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'

// What each team that can QC does, under its name.
const MODE_META: Record<string, string> = { 'Receipt into a bin': 'Receipts into a bin', Supervisor: 'Supervises' }

// Quality control (CONTROL V4 "Quality control", handoff §7i): the sets of
// qualities a QC person picks from when they scan an item. One set per
// receipting team, chosen on the team itself; this page builds the sets and
// reports which team uses which. Recorded scans keep their quality when a set
// changes or goes: only the list the app offers changes.
//
// Nothing is deleted (D7.6): a quality or a set is ARCHIVED, hidden from new scans and from the device, still named in
// history as "(archived)", and can be restored (an audited event). A set's order is the order the qualities are listed in
// the panel; moving one up or down and saving is an audited save (D7.5). Display only: it changes no figure.
//
// Dataphyll Field stations have no team: one set, chosen here, serves every station of the client and is sent to them
// in their config. No set means the stations offer no quality.

export interface Quality {
  id: string
  name: string
  deducts: boolean
  credited_pct: number
  // D7.4 C: a policy flag. Absent on an older response, which reads as optional.
  photo_requirement?: 'optional' | 'required'
}
export interface ArchivedQuality extends Quality {
  archived_at: string | null
}
export interface QualitySet {
  id: string
  name: string
  version: string
  qualities: Quality[]
  archived_qualities?: ArchivedQuality[]
  quality_count: number
  deduction_count: number
  used_by: { id: string; name: string }[]
  // The client's Field stations use this set. Absent on an older response.
  field_stations?: boolean
}
interface QcTeam {
  id: string
  name: string
  colour: string | null
  mode: string
  active: boolean
  site_name: string | null
  quality_set_id: string | null
  quality_set_name: string | null
}
interface ArchivedSet {
  id: string
  name: string
  archived_at: string | null
  archived_by: string | null
}
interface Overview {
  sets: QualitySet[]
  teams: QcTeam[]
  archived_sets?: ArchivedSet[]
  // null when the client does not use Dataphyll Field.
  field_stations?: { quality_set_id: string | null; quality_set_name: string | null; device_count: number } | null
}

// A row in the panel. `id` is the stored quality's, so an edit keeps its
// identity (and the cycles already carrying it); a new row has none.
interface DraftQuality {
  key: number
  id: string | null
  name: string
  deducts: boolean
  pct: number
}
interface Draft {
  id: string | null
  version: string | null
  name: string
  // One photo rule for the set, as Crew asks for it: every quality is saved
  // with it, and a set saved per quality opens on where any was required.
  photo: boolean
  qualities: DraftQuality[]
}

export const qualityCount = (n: number) => `${n} qualit${n === 1 ? 'y' : 'ies'}`
export const deductionLine = (n: number) => (n ? `${n} with deduction` : 'No deductions')
// Move the row with this key one place; the first cannot go up and the last cannot go down.
export function moved<T extends { key: number }>(rows: T[], key: number, by: -1 | 1): T[] {
  const i = rows.findIndex((r) => r.key === key)
  const j = i + by
  if (i < 0 || j < 0 || j >= rows.length) return rows
  const next = [...rows]
  ;[next[i], next[j]] = [next[j], next[i]]
  return next
}
// Who uses a set, in the "Used by" column and the archive warning: its teams, then the Field stations.
export const setUsers = (s: Pick<QualitySet, 'used_by' | 'field_stations'>) => [...s.used_by.map((t) => t.name), ...(s.field_stations ? ['Field stations'] : [])]
export const stationCount = (n: number) => (n ? `${n} active station${n === 1 ? '' : 's'}` : 'No active stations yet')
export const factorNote = (q: { deducts: boolean; pct: number }) => (q.deducts ? `${100 - q.pct}% deducted` : 'Full weight credited')

// V4: switching a deduction on offers half weight rather than a no-op 100%;
// switching it off credits full weight again.
export function toggled(q: DraftQuality): DraftQuality {
  return q.deducts ? { ...q, deducts: false, pct: 100 } : { ...q, deducts: true, pct: q.pct === 100 ? 50 : q.pct }
}
export function clampPct(text: string) {
  return Math.max(0, Math.min(100, parseInt(text, 10) || 0))
}

export function QualityControlPage() {
  const { user } = useAuth()
  const [data, setData] = useState<Overview | null>(null)
  // The page state that the rows on screen belong to: a first load or a new
  // period draws skeleton rows, a reload of the same period after a correction
  // does not (h 3994-4002).
  const [shown, setShown] = useState<string | null>(null)
  const loading = shown !== 'quality'
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [askDelete, setAskDelete] = useState(false)
  // The save, archive or restore in flight: only that button says so.
  const [busyKind, setBusyKind] = useState<'save' | 'delete' | 'restore' | 'field' | null>(null)
  const busy = busyKind !== null
  const slow = useSlow(busy)
  const [toast, setToast] = useState('')
  const nextKey = useRef(1)

  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const seq = useRef(0)
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const n = ++seq.current
    setError(null)
    try {
      const r = await apiJson<Overview>(u, '/api/quality')
      if (n !== seq.current) return
      setData(r)
    } catch (e: any) {
      if (n !== seq.current) return
      setError(e?.message || 'Could not load the quality sets')
    } finally {
      if (n === seq.current) setShown('quality')
    }
  }, [uid])

  useEffect(() => {
    load()
  }, [load])
  useRefetch(load)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3200)
    return () => clearTimeout(t)
  }, [toast])

  const row = (q: Partial<DraftQuality>): DraftQuality => ({ key: nextKey.current++, id: null, name: '', deducts: false, pct: 100, ...q })

  // The draft as the panel opened on it: the panel lives in this page, which
  // outlasts every set opened, so its start is taken on each open.
  const start = useRef('')
  const begin = (d: Draft) => {
    start.current = JSON.stringify(d)
    setDraft(d)
  }
  const openNew = () => begin({ id: null, version: null, name: '', photo: false, qualities: [row({})] })
  const openSet = (s: QualitySet) =>
    begin({
      id: s.id,
      version: s.version,
      name: s.name,
      photo: s.qualities.some((q) => q.photo_requirement === 'required'),
      qualities: s.qualities.map((q) => row({ id: q.id, name: q.name, deducts: q.deducts, pct: q.credited_pct })),
    })
  const close = () => {
    setDraft(null)
    setAskDelete(false)
  }

  // Layers (h 425-458, 3975-4002): the set panel asks first on Escape, the scrim and
  // Cancel once its draft has moved, and is blocked while saving (h 3975-4002);
  // the delete question over it is its own layer, so Escape there means Cancel and leaves the panel open.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = !!draft && JSON.stringify(draft) !== start.current
  const { requestClose, confirm } = useLayer(!!draft, close, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: !!draft, busy })
  const deleteEl = useRef<HTMLDivElement>(null)
  useLayer(askDelete && !!draft?.id, () => setAskDelete(false), { el: deleteEl, modal: true })
  const patchQuality = (key: number, patch: (q: DraftQuality) => DraftQuality) =>
    setDraft((d) => d && { ...d, qualities: d.qualities.map((q) => (q.key === key ? patch(q) : q)) })

  const save = async () => {
    if (!draft || busy) return
    const name = draft.name.trim()
    if (!name) return setToast('Give the set a name')
    const quals = draft.qualities.filter((q) => q.name.trim())
    if (!quals.length) return setToast('A set needs at least one quality')
    const body = {
      name,
      qualities: quals.map((q) => ({ ...(q.id ? { id: q.id } : {}), name: q.name.trim(), deducts: q.deducts, credited_pct: q.deducts ? q.pct : 100, photo_requirement: draft.photo ? 'required' : 'optional' })),
      ...(draft.id ? { version: draft.version } : {}),
    }
    setBusyKind('save')
    try {
      const r = await apiJson<Overview & { message: string }>(user, draft.id ? `/api/quality/sets/${draft.id}` : '/api/quality/sets', {
        method: draft.id ? 'PUT' : 'POST',
        body,
      })
      setData(r)
      setDraft(null)
      setToast(r.message)
    } catch (e: any) {
      setToast(e?.message || 'The set was not saved')
    } finally {
      setBusyKind(null)
    }
  }

  const confirmDelete = async () => {
    if (!draft?.id || busy) return
    setBusyKind('delete')
    try {
      // The question this call answers is the confirmation the server asks for when teams use the set.
      const r = await apiJson<Overview & { message: string }>(user, `/api/quality/sets/${draft.id}?confirm=true`, { method: 'DELETE' })
      setData(r)
      close()
      setToast(r.message)
    } catch (e: any) {
      setAskDelete(false)
      setToast(e?.message || 'The set was not archived')
    } finally {
      setBusyKind(null)
    }
  }

  const restoreSet = async (id: string) => {
    if (busy) return
    setBusyKind('restore')
    try {
      const r = await apiJson<Overview & { message: string }>(user, `/api/quality/sets/${id}/restore`, { method: 'POST', body: {} })
      setData(r)
      setToast(r.message)
    } catch (e: any) {
      setToast(e?.message || 'The set was not restored')
    } finally {
      setBusyKind(null)
    }
  }

  // Give the Field stations a set, or none ('' in the picker). The stations pick it up on their next config read.
  const chooseFieldSet = async (id: string) => {
    if (busy) return
    setBusyKind('field')
    try {
      const r = await apiJson<Overview>(user, '/api/quality/field-stations', { method: 'PUT', body: { quality_set_id: id || null } })
      setData(r)
      const name = r.field_stations?.quality_set_name
      setToast(name ? `Field stations now use ${name}` : 'Field stations no longer record quality')
    } catch (e: any) {
      setToast(e?.message || 'The Field stations set was not changed')
    } finally {
      setBusyKind(null)
    }
  }

  // Bring an archived quality back while the set is open: it goes to the end of the list, and the draft takes the
  // set's new version so the save that follows is not refused as stale. Edits already made are kept.
  const restoreQuality = async (setId: string, q: ArchivedQuality) => {
    if (busy || !draft) return
    setBusyKind('restore')
    try {
      const r = await apiJson<Overview & { message: string }>(user, `/api/quality/sets/${setId}/qualities/${q.id}/restore`, { method: 'POST', body: {} })
      setData(r)
      const set = r.sets.find((s) => s.id === setId)
      const back = row({ id: q.id, name: q.name, deducts: q.deducts, pct: q.credited_pct })
      const version = set?.version ?? draft.version
      const kept = JSON.parse(start.current) as Draft
      start.current = JSON.stringify({ ...kept, version, qualities: [...kept.qualities, back] })
      setDraft((d) => d && { ...d, version, qualities: [...d.qualities, back] })
      setToast(r.message)
    } catch (e: any) {
      setToast(e?.message || 'The quality was not restored')
    } finally {
      setBusyKind(null)
    }
  }

  const sets = data?.sets || []
  const teams = data?.teams || []
  const openSetRow = draft?.id ? sets.find((s) => s.id === draft.id) : undefined
  const deleteUsers = openSetRow ? setUsers(openSetRow).join(', ') : ''
  const field = data?.field_stations
  const archivedHere = (openSetRow?.archived_qualities || []).filter((q) => !draft?.qualities.some((x) => x.id === q.id))
  const archivedSets = data?.archived_sets || []

  return (
    <>
      <PageHeader
        title="Quality control"
        actions={
          <button type="button" className={headerStyles.btnPrimary} onClick={openNew} disabled={loading || !data}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add quality set
          </button>
        }
      />

      <PageBody>
        {error && (
          <div className={pa.errorBar} role="alert">
            <span>{error}</span>
            {!data && (
              <button type="button" className={pa.ghostSmall} onClick={load}>
                Retry
              </button>
            )}
            <button type="button" className={pa.flashX} aria-label="Dismiss" onClick={() => setError(null)}>
              ×
            </button>
          </div>
        )}

        <div className={styles.body}>
        <div className={pa.card}>
          <div className={styles.table} role="table" aria-label="Quality sets">
            <div className={styles.headRow} role="row">
              {['Quality set', 'Qualities', 'Deductions', 'Used by', ''].map((h, i) => (
                <span key={i} role="columnheader">
                  {h}
                </span>
              ))}
            </div>
            {loading ? (
              <SkeletonRows rowClass={styles.row} cols={5} label="Loading quality sets" />
            ) : sets.length === 0 ? (
              <div className={pa.empty}>{!data ? 'The quality sets could not be loaded.' : 'No quality sets yet. Add one and choose it on a receipting team.'}</div>
            ) : (
              sets.map((s) => (
                <div key={s.id} className={styles.row} role="row" data-testid="set-row" {...openableRow(() => openSet(s))}>
                  <span className={styles.setName}>{s.name}</span>
                  <span className={styles.cell}>{qualityCount(s.quality_count)}</span>
                  <span className={styles.cell}>{deductionLine(s.deduction_count)}</span>
                  <span className={setUsers(s).length ? styles.teams : styles.teamsNone}>{setUsers(s).join(', ') || 'Not used by any team'}</span>
                  <span className={styles.chev} aria-hidden>
                    ›
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {!loading && (
          <section className={styles.teamCard} aria-label="Which set each team uses">
            <div className={styles.cardTitle}>Which set each team uses</div>
            <div className={styles.cardSub}>
              Set on the team itself, in Teams. A quality set is a property of how that team receipts or supervises. Shown here so you can see the whole picture at once. Dataphyll Field stations have no team, so their one set is chosen here.
            </div>
            <div className={styles.teamList}>
              {teams.map((t) => (
                <div key={t.id} className={styles.teamRow} data-testid="team-row">
                  <span className={styles.dot} style={{ background: t.colour || 'var(--dp-muted2)' }} />
                  <div className={styles.teamText}>
                    <span className={styles.teamName}>{t.name}</span>
                    <span className={styles.teamMeta}>
                      {[t.site_name, MODE_META[t.mode] || 'Receipts items', t.active ? null : 'Inactive'].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <span className={t.quality_set_name ? styles.teamValue : styles.teamValueNone}>{t.quality_set_name || 'Not allowed to QC'}</span>
                  <Link to={`/teams?team=${encodeURIComponent(t.id)}`} className={styles.openTeam} title="Open this team to change its quality set">
                    Open team
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                </div>
              ))}
              {field && (
                <div className={styles.teamRow} data-testid="field-row">
                  <span className={styles.dot} style={{ background: 'var(--dp-muted2)' }} />
                  <div className={styles.teamText}>
                    <span className={styles.teamName}>Field stations</span>
                    <span className={styles.teamMeta}>Dataphyll Field · {stationCount(field.device_count)} · one set for every station</span>
                  </div>
                  <select
                    className={styles.fieldSelect}
                    value={field.quality_set_id || ''}
                    disabled={busy}
                    onChange={(e) => chooseFieldSet(e.target.value)}
                    aria-label="Quality set for Field stations"
                  >
                    <option value="">No quality control</option>
                    {sets.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {teams.length === 0 && (
                <div className={styles.noTeams}>No team is set to receipt or supervise yet. Set a team's Dataphyll Crew App mode to receipting or Supervisor and it will appear here.</div>
              )}
            </div>
          </section>
        )}

        {!loading && archivedSets.length > 0 && (
          <section className={styles.teamCard} aria-label="Archived quality sets" data-testid="archived-sets">
            <div className={styles.cardTitle}>Archived quality sets</div>
            <div className={styles.cardSub}>Kept, not deleted. Restoring one does not give it back to its teams: choose it on the team again.</div>
            <div className={styles.teamList}>
              {archivedSets.map((s) => (
                <div key={s.id} className={styles.teamRow}>
                  <div className={styles.teamText}>
                    <span className={styles.teamName}>{s.name} <em>(archived)</em></span>
                    <span className={styles.teamMeta}>{[s.archived_by, s.archived_at ? new Date(s.archived_at).toLocaleDateString('en-NZ') : null].filter(Boolean).join(' · ')}</span>
                  </div>
                  <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => restoreSet(s.id)} aria-label={`Restore ${s.name}`}>
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {draft && (
        <div className={o.scrim} onClick={requestClose}>
          <div ref={panelEl} className={`${o.modal} ${o.wide}`} role="dialog" aria-label={draft.id ? 'Quality set' : 'New quality set'} onClick={(e) => e.stopPropagation()}>
            <div className={`${o.head} ${inertClass(busy, slow)}`}>
              <div className={o.headText}>
                <h2 className={o.title}>{draft.id ? 'Quality set' : 'New quality set'}</h2>
              </div>
              <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className={`${o.body} ${inertClass(busy, slow)}`}>
              <div className={o.field}>
                <label className={o.fieldLabel}>Set name</label>
                <input className={o.input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Blueberry Qualities" />
              </div>
              <div className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>Photo required</div>
                  <div className={o.toggleSub}>Every QC scan on a team with this set asks for a photo. A scan without one is kept and applied, and flagged as photo missing.</div>
                </div>
                <button type="button" role="switch" aria-checked={draft.photo} aria-label="Photo required" className={o.switch} onClick={() => setDraft({ ...draft, photo: !draft.photo })} />
              </div>
              <div className={styles.qGrid + ' ' + styles.qHead}>
                <span>Quality</span>
                <span>Deduction</span>
                <span>Credited weight</span>
                <span />
              </div>
              <div className={styles.qList}>
                {draft.qualities.map((q, i) => (
                  <div key={q.key} className={styles.qGrid} data-testid="quality-row">
                    <input
                      className={styles.qInput}
                      value={q.name}
                      onChange={(e) => patchQuality(q.key, (x) => ({ ...x, name: e.target.value }))}
                      placeholder="e.g. Picking damage"
                      aria-label="Quality name"
                    />
                    <button
                      type="button"
                      role="switch"
                      aria-checked={q.deducts}
                      aria-label={`Deduction for ${q.name || 'this quality'}`}
                      className={q.deducts ? styles.switchOn : styles.switch}
                      onClick={() => patchQuality(q.key, toggled)}
                    >
                      <span className={styles.knob} />
                    </button>
                    <div className={styles.credit}>
                      {q.deducts && (
                        <div className={styles.pctRow}>
                          <input
                            className={styles.pctInput}
                            value={String(q.pct)}
                            onChange={(e) => patchQuality(q.key, (x) => ({ ...x, pct: clampPct(e.target.value) }))}
                            inputMode="numeric"
                            aria-label={`Credited weight for ${q.name || 'this quality'}`}
                          />
                          <span className={styles.pctSign}>%</span>
                        </div>
                      )}
                      <span className={q.deducts ? styles.noteDeduct : styles.noteFull}>{factorNote(q)}</span>
                    </div>
                    <div className={styles.rowTools}>
                      <button
                        type="button"
                        className={styles.moveBtn}
                        title="Move up"
                        aria-label={`Move ${q.name || 'this quality'} up`}
                        disabled={i === 0}
                        onClick={() => setDraft({ ...draft, qualities: moved(draft.qualities, q.key, -1) })}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className={styles.moveBtn}
                        title="Move down"
                        aria-label={`Move ${q.name || 'this quality'} down`}
                        disabled={i === draft.qualities.length - 1}
                        onClick={() => setDraft({ ...draft, qualities: moved(draft.qualities, q.key, 1) })}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className={styles.removeBtn}
                        title={q.id ? 'Archive this quality: it is kept, hidden from new scans, and can be restored' : 'Remove this quality'}
                        aria-label={`${q.id ? 'Archive' : 'Remove'} ${q.name || 'this quality'}`}
                        onClick={() => setDraft({ ...draft, qualities: draft.qualities.filter((x) => x.key !== q.key) })}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.addQual} onClick={() => setDraft({ ...draft, qualities: [...draft.qualities, row({})] })}>
                Add quality
              </button>
              {draft.id && archivedHere.length > 0 && (
                <div className={styles.archived} data-testid="archived-qualities">
                  <div className={styles.archivedTitle}>Archived qualities</div>
                  <div className={styles.archivedNote}>Hidden from new scans and from the device. Scans already recorded keep them.</div>
                  {archivedHere.map((q) => (
                    <div key={q.id} className={styles.archivedRow}>
                      <span>
                        {q.name} <em>(archived)</em> · {factorNote({ deducts: q.deducts, pct: q.credited_pct })}
                      </span>
                      <button type="button" className={pa.ghostBtn} disabled={busy} onClick={() => restoreQuality(draft.id as string, q)} aria-label={`Restore ${q.name}`}>
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={o.foot}>
              <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
                Cancel
              </button>
              {draft.id && (
                <button type="button" className={styles.deleteBtn} disabled={busy} onClick={() => setAskDelete(true)}>
                  Archive set
                </button>
              )}
              <PendingButton
                className={o.primary}
                busy={busyKind === 'save'}
                disabled={busy}
                pending={draft.id ? 'Saving…' : 'Creating…'}
                onClick={save}
              >
                {draft.id ? 'Save set' : 'Create set'}
              </PendingButton>
            </div>
          </div>
          {confirm}
        </div>
      )}

      {askDelete && draft?.id && (
        <div className={o.scrim} onClick={() => !busy && setAskDelete(false)}>
          <div ref={deleteEl} className={o.modal} role="alertdialog" aria-label={`Archive ${openSetRow?.name || draft.name}?`} onClick={(e) => e.stopPropagation()}>
            <div className={o.head}>
              <div className={o.headText}>
                <h2 className={o.title}>Archive {openSetRow?.name || draft.name}?</h2>
              </div>
              <button type="button" className={o.close} aria-label="Close" onClick={() => setAskDelete(false)}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className={o.body}>
              <p>
                Quality scans already recorded keep the quality they were given. The set is kept, hidden from new scans and from the device, and can be restored. A scan that
                arrives for it after this is held for review.
              </p>
              {deleteUsers && <div className={o.warn}>{deleteUsers} will be left unable to record quality at all.</div>}
            </div>
            <div className={o.foot}>
              <button type="button" className={o.ghost} disabled={busy} onClick={() => setAskDelete(false)}>
                Cancel
              </button>
              <PendingButton className={o.dangerBtn} busy={busyKind === 'delete'} pending="Archiving…" onClick={confirmDelete}>
                Archive set
              </PendingButton>
            </div>
          </div>
        </div>
      )}

        {toast && (
          <div className={pa.toast} role="status">
            {toast}
          </div>
        )}
      </PageBody>
    </>
  )
}
